import React from 'react';
import {
  CubeIcon,
  MapPinIcon,
  CalendarIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
  XMarkIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { apiGet, getAuthToken } from '../../utils/api';
import { API_URL } from '../../api/config';
import MapBaseComponent from '../maps/MapBaseComponent';
import { RWANDA_DEFAULTS, calculateCenter } from '../../utils/geospatial';

interface CropDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  cropData: any;
}

const parseBoundary = (value: any) => {
  if (!value) return [];
  let raw = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((point: any) => Array.isArray(point)
      ? { lat: Number(point[0]), lng: Number(point[1]) }
      : { lat: Number(point.lat ?? point.latitude), lng: Number(point.lng ?? point.longitude) })
    .filter((point: any) => Number.isFinite(point.lat) && Number.isFinite(point.lng) && point.lat !== 0 && point.lng !== 0);
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'fields', label: 'Fields' },
  { id: 'activities', label: 'Activities' },
  { id: 'observations', label: 'Observations' },
  { id: 'harvest', label: 'Harvest' },
  { id: 'map', label: 'Map' },
  { id: 'exports', label: 'Exports' },
] as const;

type TabId = typeof TABS[number]['id'];

const CropDetailModal: React.FC<CropDetailModalProps> = ({ isOpen, onClose, cropData }) => {
  const [activeTab, setActiveTab] = React.useState<TabId>('overview');
  const [mapCenter, setMapCenter] = React.useState(RWANDA_DEFAULTS.center);
  const [mapZoom, setMapZoom] = React.useState(RWANDA_DEFAULTS.zoom);

  const fields = Array.isArray(cropData?.fields) ? cropData.fields : [];
  const farmers = Array.isArray(cropData?.farmers) ? cropData.farmers : [];
  const agronomists = Array.isArray(cropData?.agronomists) ? cropData.agronomists : [];
  const sales = Array.isArray(cropData?.sales) ? cropData.sales : [];
  const activities = Array.isArray(cropData?.activities) ? cropData.activities : [];
  const observations = Array.isArray(cropData?.observations) ? cropData.observations : [];
  const ops = cropData?.operations || {};
  const crop = cropData?.crop || {};

  React.useEffect(() => {
    if (!isOpen) return;
    const withCoords = fields.filter((f: any) => parseBoundary(f.boundary_coordinates).length > 0);
    const centers = withCoords.map((f: any) => ({
      lat: Number(f.center_lat ?? f.center_latitude) || 0,
      lng: Number(f.center_lng ?? f.center_longitude) || 0,
    })).filter((c: any) => c.lat && c.lng);
    const allCoords = withCoords.flatMap((f: any) => parseBoundary(f.boundary_coordinates));
    const pool = centers.length > 0 ? centers : allCoords;
    if (pool.length > 0) {
      setMapCenter(calculateCenter(pool) as any);
      setMapZoom(11);
    } else {
      setMapCenter(RWANDA_DEFAULTS.center);
      setMapZoom(RWANDA_DEFAULTS.zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cropData]);

  const downloadCropCsv = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/crops/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crops.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Export failed');
    }
  };

  const downloadCsv = (name: string, header: string[], rows: any[][]) => {
    const csv = [header, ...rows]
      .map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency: 'RWF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (!isOpen || !cropData) return null;

  const statusColor = (status: string) => {
    const map: Record<string, string> = {
      approved: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      submitted: 'bg-amber-100 text-amber-800',
      awaiting_review: 'bg-amber-100 text-amber-800',
      correction_required: 'bg-red-100 text-red-800',
      assigned: 'bg-emerald-100 text-emerald-800',
      draft: 'bg-gray-100 text-gray-700',
      closed: 'bg-slate-100 text-slate-600',
      rejected: 'bg-red-100 text-red-800',
      overdue: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const priorityColor = (priority: string) => {
    const map: Record<string, string> = { urgent: 'text-red-700', high: 'text-orange-700', normal: 'text-slate-600', low: 'text-slate-500' };
    return map[priority] || 'text-slate-600';
  };

  const mappedFields = fields.filter((f: any) => parseBoundary(f.boundary_coordinates).length > 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white p-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{crop.product_type}</h2>
              <p className="text-emerald-100 text-xs capitalize">{crop.status || 'active'} crop • {fields.length} fields • {farmers.length} farmers</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-2">✕</button>
          </div>
        </div>

        <div className="sticky top-0 bg-white border-b border-gray-200 flex gap-1 px-4 py-2 overflow-x-auto z-10">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 font-medium">Crop Type</p>
                  <p className="text-sm font-bold text-emerald-900">{crop.product_type}</p>
                  {crop.description && <p className="mt-1 text-xs text-emerald-700">{crop.description}</p>}
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">Mapped Area (ha)</p>
                  <p className="text-sm font-bold text-blue-900">{fields.reduce((s: number, f: any) => s + parseFloat(f.area_hectares || 0), 0).toFixed(2)}</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-600 font-medium">Farmers</p>
                  <p className="text-sm font-bold text-amber-900">{farmers.length}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium">Trees Mapped</p>
                  <p className="text-sm font-bold text-purple-900">{formatNumber(fields.reduce((s: number, f: any) => s + (f.mapped_tree_count || 0), 0))}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-xs text-green-600 font-medium">Production (kg)</p>
                  <p className="text-sm font-bold text-green-900">{formatNumber(cropData.performance?.totalProduction || 0)}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 font-medium">Revenue</p>
                  <p className="text-sm font-bold text-emerald-900">{formatCurrency(cropData.performance?.totalRevenue || 0)}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 font-medium">Input Costs</p>
                  <p className="text-sm font-bold text-red-900">{formatCurrency(cropData.expenses?.input_costs || 0)}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-600 font-medium">Harvest Window</p>
                  <p className="text-sm font-bold text-slate-900">{crop.harvest_date ? new Date(crop.harvest_date).toLocaleDateString() : '—'}</p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1">
                  <ClipboardDocumentListIcon className="w-4 h-4 text-emerald-600" /> Operations
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-xs text-gray-500">Assignments</p>
                    <p className="text-lg font-black text-gray-900">{ops.total_assignments || 0}</p>
                    <p className="text-xs text-emerald-600">{ops.in_progress_assignments || 0} in progress</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-xs text-gray-500">Awaiting Review</p>
                    <p className="text-lg font-black text-amber-600">{ops.awaiting_review || 0}</p>
                    <p className="text-xs text-gray-500">{ops.reports_awaiting_review || 0} reports</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-xs text-gray-500">Observations</p>
                    <p className="text-lg font-black text-gray-900">{ops.total_observations || 0}</p>
                    <p className="text-xs text-red-600">{ops.alert_observations || 0} alerts</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-xs text-gray-500">Next Due</p>
                    <p className="text-sm font-black text-gray-900">{ops.next_due_date ? new Date(ops.next_due_date).toLocaleDateString() : '—'}</p>
                  </div>
                </div>
              </div>

              {sales.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Sales Snapshot</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-gray-200 rounded-lg">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Farmer</th>
                          <th className="px-3 py-2 text-right">Qty (kg)</th>
                          <th className="px-3 py-2 text-right">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sales.slice(0, 5).map((sale: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2">{new Date(sale.sale_date).toLocaleDateString()}</td>
                            <td className="px-3 py-2">{sale.farmer_name}</td>
                            <td className="px-3 py-2 text-right">{formatNumber(sale.quantity_kg)}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-600">{formatCurrency(sale.gross_revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserGroupIcon className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-sm font-bold text-gray-900">Farmers</h3>
                  </div>
                  <div className="space-y-2">
                    {farmers.slice(0, 5).map((farmer: any) => (
                      <div key={farmer.id} className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{farmer.full_name || `Farmer ${farmer.id}`}</p>
                          <p className="text-xs text-gray-500">{farmer.assigned_tree_count || 0} trees</p>
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-emerald-700">{farmer.mapped_tree_count || 0} mapped</span>
                      </div>
                    ))}
                    {farmers.length === 0 && <p className="text-sm text-gray-500">No farmers growing this crop yet.</p>}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrophyIcon className="w-5 h-5 text-blue-600" />
                    <h3 className="text-sm font-bold text-gray-900">Agronomists</h3>
                  </div>
                  <div className="space-y-2">
                    {agronomists.slice(0, 5).map((user: any) => (
                      <div key={user.id} className="flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{user.full_name || user.email}</p>
                          <p className="text-xs text-gray-500">{user.farmer_count || 0} farmers supervised</p>
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-blue-700">{user.assigned_tree_count || 0} trees</span>
                      </div>
                    ))}
                    {agronomists.length === 0 && <p className="text-sm text-gray-500">No agronomist assigned yet.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div>
              {fields.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium">Fields</p>
                      <p className="text-xl font-bold text-blue-900">{fields.length}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600 font-medium">Area (ha)</p>
                      <p className="text-xl font-bold text-green-900">{fields.reduce((s: number, f: any) => s + parseFloat(f.area_hectares || 0), 0).toFixed(1)}</p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-600 font-medium">Mapped Trees</p>
                      <p className="text-xl font-bold text-amber-900">{formatNumber(fields.reduce((s: number, f: any) => s + (f.mapped_tree_count || 0), 0))}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {fields.map((field: any, idx: number) => (
                      <div key={field.id || idx} className="bg-white border-2 border-emerald-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm">{field.name || field.plot_code || `Field ${idx + 1}`}</h4>
                            <p className="text-xs text-gray-600">{field.farmer_name || '—'}</p>
                            <p className="text-xs text-gray-500">Agronomist: {field.assigned_agronomist_name || 'Not assigned'}</p>
                          </div>
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs font-semibold">
                            {field.plot_code || `#${field.id}`}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          <div>
                            <p className="text-gray-600 font-medium">Area (ha)</p>
                            <p className="font-bold text-gray-900">{parseFloat(field.area_hectares || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 font-medium">Trees</p>
                            <p className="font-bold text-gray-900">{field.tree_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 font-medium">Mapped</p>
                            <p className="font-bold text-blue-600">{field.mapped_tree_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 font-medium">Expected</p>
                            <p className="font-bold text-green-600">{field.expected_tree_count || 0}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MapPinIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No mapped fields for {crop.product_type} yet. Create them on the Farm Maps page.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activities' && (
            <div>
              {activities.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium">Total</p>
                      <p className="text-sm font-bold text-blue-900">{ops.total_assignments || 0}</p>
                    </div>
                    <div className="bg-emerald-50 p-2 rounded-lg border border-emerald-200">
                      <p className="text-xs text-emerald-600 font-medium">Open</p>
                      <p className="text-sm font-bold text-emerald-900">{ops.open_assignments || 0}</p>
                    </div>
                    <div className="bg-amber-50 p-2 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-600 font-medium">Review</p>
                      <p className="text-sm font-bold text-amber-900">{ops.awaiting_review || 0}</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600 font-medium">Approved</p>
                      <p className="text-sm font-bold text-green-900">{ops.approved_assignments || 0}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-gray-200 rounded-lg">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left">Code</th>
                          <th className="px-3 py-2 text-left">Title</th>
                          <th className="px-3 py-2 text-left">Field</th>
                          <th className="px-3 py-2 text-left">Farmer</th>
                          <th className="px-3 py-2 text-right">Due</th>
                          <th className="px-3 py-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activities.map((a: any, i: number) => (
                          <tr key={a.id || i} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-blue-600">{a.code}</td>
                            <td className="px-3 py-2 font-semibold text-gray-900">
                              {a.title}
                              <span className={`block text-[10px] uppercase tracking-wide ${priorityColor(a.priority)}`}>{a.priority}</span>
                            </td>
                            <td className="px-3 py-2">{a.field_name || a.plot_code || '—'}</td>
                            <td className="px-3 py-2">{a.farmer_name || a.assigned_to_name || '—'}</td>
                            <td className="px-3 py-2 text-right">{a.due_date ? new Date(a.due_date).toLocaleDateString() : '—'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-xs px-2 py-1 rounded font-semibold ${statusColor(a.status)}`}>
                                {String(a.status || 'draft').replace('_', ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardDocumentListIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No field activities for {crop.product_type} yet. Assign work on the Activities page.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'observations' && (
            <div>
              {observations.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-600 font-medium">Total</p>
                      <p className="text-sm font-bold text-slate-900">{ops.total_observations || 0}</p>
                    </div>
                    <div className="bg-red-50 p-2 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600 font-medium">Alerts</p>
                      <p className="text-sm font-bold text-red-900">{ops.alert_observations || 0}</p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium">Pest/Disease</p>
                      <p className="text-sm font-bold text-blue-900">
                        {observations.filter((o: any) => o.pest_presence || o.disease_presence).length}
                      </p>
                    </div>
                  </div>
                  {observations.map((obs: any, i: number) => (
                    <div key={obs.id || i} className={`rounded-xl border p-3 ${obs.alert_triggered ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {obs.alert_triggered
                            ? <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                            : <CheckCircleIcon className="w-5 h-5 text-emerald-600" />}
                          <div>
                            <p className="text-sm font-bold text-gray-900 capitalize">
                              {obs.field_name || obs.plot_code || `Field #${obs.field_plot_id || '—'}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {obs.farmer_name || '—'} • {new Date(obs.created_at).toLocaleDateString()} • {obs.recorded_by_name || '—'}
                            </p>
                          </div>
                        </div>
                        {obs.alert_triggered && (
                          <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-bold">ALERT</span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        {obs.crop_stage && <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700 capitalize">{obs.crop_stage}</span>}
                        {obs.crop_condition && <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700 capitalize">{obs.crop_condition}</span>}
                        {obs.pest_presence && <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800">Pests: {obs.pest_type || 'yes'}</span>}
                        {obs.disease_presence && <span className="rounded-full bg-purple-100 px-2 py-1 text-purple-800">Disease: {obs.disease_type || 'yes'}</span>}
                        {obs.severity_level && <span className="rounded-full bg-red-100 px-2 py-1 text-red-700 capitalize">Severity: {obs.severity_level}</span>}
                      </div>
                      {obs.recommended_follow_up && (
                        <p className="mt-2 text-xs text-gray-600">Follow-up: {obs.recommended_follow_up}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <EyeIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No field observations for {crop.product_type} yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'harvest' && (
            <div>
              {sales.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="bg-green-50 p-2 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600 font-medium">Sales</p>
                      <p className="text-sm font-bold text-green-900">{sales.length}</p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium">Total Qty (kg)</p>
                      <p className="text-sm font-bold text-blue-900">{formatNumber(sales.reduce((s: number, d: any) => s + (d.quantity_kg || 0), 0))}</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded-lg border border-purple-200">
                      <p className="text-xs text-purple-600 font-medium">Revenue</p>
                      <p className="text-sm font-bold text-purple-900">{formatCurrency(sales.reduce((s: number, d: any) => s + (d.gross_revenue || 0), 0))}</p>
                    </div>
                    <div className="bg-amber-50 p-2 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-600 font-medium">Avg Price/kg</p>
                      <p className="text-sm font-bold text-amber-900">{formatCurrency(sales.length > 0 ? sales.reduce((s: number, d: any) => s + (d.unit_price || 0), 0) / sales.length : 0)}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-gray-200 rounded-lg">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Order #</th>
                          <th className="px-3 py-2 text-left">Farmer</th>
                          <th className="px-3 py-2 text-right">Qty (kg)</th>
                          <th className="px-3 py-2 text-right">Price/kg</th>
                          <th className="px-3 py-2 text-right">Revenue</th>
                          <th className="px-3 py-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sales.map((sale: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2">{new Date(sale.sale_date).toLocaleDateString()}</td>
                            <td className="px-3 py-2 font-semibold text-blue-600">{sale.order_number}</td>
                            <td className="px-3 py-2">{sale.farmer_name}</td>
                            <td className="px-3 py-2 text-right">{formatNumber(sale.quantity_kg)}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(sale.unit_price)}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-600">{formatCurrency(sale.gross_revenue)}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="text-xs px-2 py-1 rounded font-semibold capitalize bg-gray-100 text-gray-800">{sale.order_status || '—'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CurrencyDollarIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No harvest/sales records for {crop.product_type} yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'map' && (
            <div className="space-y-3">
              {mappedFields.length > 0 ? (
                <div className="h-[480px] rounded-lg overflow-hidden border border-gray-200">
                  <MapBaseComponent
                    farmers={[]}
                    fieldPlots={mappedFields.map((f: any) => ({
                      id: f.id,
                      name: f.name || f.plot_code || `Field ${f.id}`,
                      plot_code: f.plot_code,
                      household_type: 'field_plot',
                      plot_boundary_coordinates: parseBoundary(f.boundary_coordinates),
                      cohort_name: crop.product_type,
                      cohort_id: 0,
                      plot_area_hectares: Number(f.area_hectares || 0),
                    }))}
                    cohorts={[]}
                    warehouses={[]}
                    center={mapCenter as any}
                    zoom={mapZoom}
                    showLocationTracker={false}
                  />
                </div>
              ) : (
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border-2 border-dashed border-emerald-300 p-6 text-center flex flex-col items-center justify-center min-h-[300px]">
                  <MapPinIcon className="w-12 h-12 text-emerald-400 mb-2" />
                  <p className="text-sm font-semibold text-gray-700">No Field Boundaries Yet</p>
                  <p className="text-xs text-gray-500 mt-1">Sketch farm boundaries on the Farm Maps page to see them here.</p>
                </div>
              )}
              {mappedFields.length > 0 && (
                <div className="bg-white rounded-lg p-3 border border-gray-200 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                  <span className="font-semibold text-gray-700">Legend</span>
                  <span className="flex items-center gap-2"><span className="inline-block w-5 h-2 rounded-sm bg-yellow-300 border border-yellow-400" /> Field boundary</span>
                  <span>{mappedFields.length} fields</span>
                  <span>{mappedFields.reduce((s: number, f: any) => s + parseFloat(f.area_hectares || 0), 0).toFixed(1)} ha total</span>
                </div>
              )}
            </div>
          )}

          {activeTab === 'exports' && (
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Export Options</h3>
                <p className="text-xs text-gray-500 mb-4">Download crop data for {crop.product_type}.</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={downloadCropCsv}
                    className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700 transition-colors"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                    All Crops (CSV)
                  </button>
                  <button
                    onClick={() => downloadCsv(
                      `${cropKey(crop.product_type)}-fields.csv`,
                      ['plot_code', 'name', 'area_hectares', 'tree_count', 'mapped_tree_count', 'expected_tree_count', 'agronomist', 'farmer'],
                      fields.map((f: any) => [f.plot_code, f.name, f.area_hectares, f.tree_count, f.mapped_tree_count, f.expected_tree_count, f.assigned_agronomist_name, f.farmer_name])
                    )}
                    disabled={fields.length === 0}
                    className="flex items-center gap-2 rounded-xl border border-emerald-300 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                    Fields (CSV)
                  </button>
                  <button
                    onClick={() => downloadCsv(
                      `${cropKey(crop.product_type)}-activities.csv`,
                      ['code', 'title', 'status', 'priority', 'due_date', 'field', 'farmer'],
                      activities.map((a: any) => [a.code, a.title, a.status, a.priority, a.due_date, a.field_name, a.farmer_name])
                    )}
                    disabled={activities.length === 0}
                    className="flex items-center gap-2 rounded-xl border border-emerald-300 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                    Activities (CSV)
                  </button>
                  <button
                    onClick={() => downloadCsv(
                      `${cropKey(crop.product_type)}-observations.csv`,
                      ['field', 'farmer', 'crop_stage', 'crop_condition', 'pest_presence', 'disease_presence', 'severity_level', 'alert_triggered', 'follow_up', 'recorded_by', 'created_at'],
                      observations.map((o: any) => [o.field_name, o.farmer_name, o.crop_stage, o.crop_condition, o.pest_presence, o.disease_presence, o.severity_level, o.alert_triggered, o.recommended_follow_up, o.recorded_by_name, o.created_at])
                    )}
                    disabled={observations.length === 0}
                    className="flex items-center gap-2 rounded-xl border border-emerald-300 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                    Observations (CSV)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const cropKey = (value?: string | null) =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export default CropDetailModal;
